import { Box, Grid, Stack, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";

import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ColorScheme } from "../../../Themes/ColorScheme";
import { ApiContext } from "../../ApiContext";
import { UserContext } from "../../UserContext";
import { CustomizeButton } from "./Buttons/CustomizeButton";
import { FormCancel } from "./Buttons/FormCancel";
import { FormEdit } from "./Buttons/FormEdit";
import { FormSave } from "./Buttons/FormSave";

import { ReactComponent as LinkIcon } from "../../../Assets/Icons/LinkIcon.svg";
import { AlertBar } from "../../Common/AlertBar";

export const TextDetail = ({ data, refreshData }) => {
	const debug = false;

	const [editMode, setEditMode] = useState(false);
	const [descriptionText, setDescriptionText] = useState("");
	const [nameText, setNameText] = useState("");
	const [websitesText, setWebsitesText] = useState("");
	const [hidden, setHidden] = useState();

	const [saving, setSaving] = useState(false);

	const { selectedLocation } = useContext(UserContext);

	const { t } = useTranslation();
	const { ApiCall } = useContext(ApiContext);

	const handleSave = () => {
		setSaving(true);
		ApiCall("PUT", `client/location/${data.id}`, handleAxiosBody)
			.then(() => {
				setEditMode(!editMode);
				refreshData(data.id);
				setSaving(false);
			})
			.catch((error) => {
				if (debug) console.log(error);
				setSaving(false);
			});
	};

	const handleAxiosBody = {
		description:
			data.description !== descriptionText
				? descriptionText
				: data.description,
		name: data.name !== nameText ? nameText : data.name,
		website:
			data.website !== websitesText ? websitesText : data.website,
		is_hidden: data.is_hidden !== hidden ? hidden : data.is_hidden
	};

	const cancelAction = () => {
		setDescriptionText(data.description);
		setNameText(data.name);
		setWebsitesText(data.website);
		setEditMode(!editMode);
		setHidden(data.is_hidden);
	};

	useEffect(() => {
		setEditMode(false);
		setDescriptionText(data.description);
		setNameText(data.name);
		setWebsitesText(data.website);
		setHidden(data.is_hidden);
	}, [selectedLocation]);

	useEffect(() => {
		let isHidden = selectedLocation.is_hidden;
		let hasCharging = selectedLocation?.location_permissions?.some(perm => perm.permission_id === 7);
		let hasWebsite = selectedLocation?.website != null;

		if (!isHidden) {
			setHidden(false);
		} else {
			setHidden(true);
		}

	}, [selectedLocation]);

	return (
		<Stack
			direction="row"
			justifyContent="space-between"
			sx={{
				/* width: isMobile ? "95dvw" : "100%", */
				border: editMode ? "1px dashed " + ColorScheme.white : "0px",
				borderRadius: "4px",
				px: "15px",
				paddingBottom: "15px"
				/* paddingRight: "20px" */
			}}
		>
			<div
				style={{
					flexDirection: "row",
					display: "flex",
					flex: 1,
					justifyContent: "flex-start"
				}}
			>
				<Stack
					spacing={3}
					sx={{
						width: "100%"
					}}
				>
					<Stack spacing={1} direction="row">
						<Stack
							spacing={2}
							sx={{
								width: "100%"
							}}
						>
							<Box>
								<Typography
									sx={{
										fontSize: isMobile ? 30 : 40,
										marginBottom: "5px",
										fontWeight: "700",
										lineHeight: 1.2
									}}
								>
									{editMode ? (
										<FormEdit
											title={t(
												"location.form_location_name_label"
											)}
											value={nameText}
											isRequired={true}
											changeData={(value) =>
												setNameText(value)
											}
										/>
									) : (
										nameText
									)}
								</Typography>
							</Box>
							<Typography variant="form_text_desc">
								{editMode ? (
									<FormEdit
										title={t(
											"location.form_location_description_label"
										)}
										value={descriptionText}
										type={"text"}
										isMultiRow={true}
										hasCharLimit={true}
										changeData={(value) =>
											setDescriptionText(value)
										}
									/>
								) : (
									descriptionText
								)}
							</Typography>
							{editMode && (
								<FormEdit
									title={t(
										"location.form_location_website_label"
									)}
									value={websitesText}
									type={"text"}
									hasCharLimit={true}
									isMultiRow={false}
									changeData={(value) =>
										setWebsitesText(value)
									}
								/>
							)}
							{!editMode && data.website && (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<LinkIcon
										fill={ColorScheme.transparent}
										fillOpacity={1}
										fillRule="nonzero"
										stroke={ColorScheme.secondary}
										width={"16px"}
										heigth={"16px"}
										strokeWidth={"2px"}
									/>
									<Typography sx={{ fontSize: 14, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }} onClick={() => window.open(data.website, "_blank")}>
										{data.website}
									</Typography>
								</Box>
							)}
							<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, marginTop: "10px" }}>
								<Box
									onClick={() => {
										if (editMode) {
											setHidden(!hidden);
										}
									}}
									sx={{
										display: "inline-block", // Add this line
										backgroundColor: hidden
											? ColorScheme.dangerSecondary
											: ColorScheme.successSecondary,
										border: "1px solid " + (hidden ? ColorScheme.dangerPrimary : ColorScheme.successPrimary),	
										borderRadius: "4px",
										marginRight: "20px",
										height: "auto",
										width: "auto",
										padding: "5px 10px",
										cursor: editMode ? "pointer" : "default",
										whiteSpace: "nowrap",
									}}
								>
									<Typography sx={{
										color: hidden ? ColorScheme.dangerPrimary : ColorScheme.successPrimary,
										fontSize: "10px",
										fontWeight: "700",
									}}>
										{hidden ? t("location.hidden_from_all") : t("location.visible_to_all")}
									</Typography>
								</Box>
								{editMode ? (
									<AlertBar
										textContent={
											t(
														"location.location_visibility_info_text"
													)
										}
										type="info"
										showAlert={true}
										standardVariant={"standard"}
									/>
								)
								: <div style={{flex: 1}}></div>
								}
							</Box>
						</Stack>
					</Stack>

					<Grid container spacing={1}>
						{Object.prototype.hasOwnProperty.call(
							data,
							"location_permissions"
						) &&
							data.location_permissions.map(
								(permission, index) => (
									<Grid
										sx={{
											width: "max-content",
											height: "max-content",

											padding: "5px",

											px: "15px",
											backgroundColor:
												permission.permission.color,
											marginTop: "10px",
											borderRadius: "21px",
											marginRight: "10px",
											alignItems: "center"
										}}
										key={index}
									>
										<Typography
											style={{
												fontSize: "12px",
												fontWeight: "400",
												lineHeight: "14px",
												color: ColorScheme.secondary,
												textAlign: "center"
											}}
										>
											{permission.permission.name_fi}
										</Typography>
									</Grid>
								)
							)}
					</Grid>

					{editMode && (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								justifyContent: "flex-end"
							}}
						>
							<FormCancel disabled={saving} cancelAction={cancelAction} />
							<FormSave
								check={nameText === ""}
								handleSave={handleSave}
								activity={saving}
							/>
						</Box>
					)}
				</Stack>
			</div>
			{!editMode && (
				<CustomizeButton
					changeEditMode={() => setEditMode(!editMode)}
				/>
			)}
		</Stack>
	);
};
